const normalize = str => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

class ShirtQuiz extends HTMLElement {
  constructor() {
    super();
    this.steps = this.querySelector('[data-steps]');
    this.inputs = this.querySelectorAll('[data-step] input');
    this.nextStepButtons = this.querySelectorAll('[data-next-step]');
    this.previousStepButtons = this.querySelectorAll('[data-previous-step]');
    this.form = this.querySelector('form');
    this.collectionUrl = window.shirtQuiz.allCollectionUrl;
    this.totalCount = this.querySelector('#shirt-quiz-total-products');
  }

  controller = undefined;

  connectedCallback() {
    this.update = this.update.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.previousStep = this.previousStep.bind(this);

    this.nextStepButtons.forEach(button => button.addEventListener('click', this.nextStep));
    this.previousStepButtons.forEach(button => button.addEventListener('click', this.previousStep));
    this.inputs.forEach(input => input.addEventListener('change', this.update));
    this.form.addEventListener('submit', this.submit);
  }

  submit() {
    // On form submit, publish to ShopifyAnalytics that the quiz is finished
    Shopify.analytics.publish('shirt_quiz_finished');
  }

  disconnectedCallback() {
    this.nextStepButtons.forEach(button => button.removeEventListener('click', this.nextStep));
    this.previousStepButtons.forEach(button => button.removeEventListener('click', this.previousStep));
    this.inputs.forEach(input => input.removeEventListener('change', this.update));
    this.form.removeEventListener('submit', this.submit);
  }

  async fetchFilters(options = {}) {
    const queryString = new URLSearchParams(new FormData(this.form)).toString();
    const res = await fetch(`${this.collectionUrl}?section_id=fetch-filters&${queryString}`, options);
    const text = await res.text();
    const html = new DOMParser().parseFromString(text, 'text/html');
    const shopifySection = html.querySelector('.shopify-section');
    if (!shopifySection) throw new Error('No shopify section found');

    const jsonFilters = JSON.parse(shopifySection.innerHTML);
    if (!jsonFilters) throw new Error('No JSON found');

    return jsonFilters;
  }

  /**
   * 
   * @param {Event | HTMLElement} event 
   */
  async nextStep(event) {
    let label = null;
    if (event instanceof Event) {
      event.preventDefault();
      label = event.currentTarget;
    } else {
      label = event;
    }

    let shouldStopLoading = true;

    const { nextStep, filterKey } = label.dataset;
    const nextStepElement = this.querySelector(`#quiz_step_${nextStep}`);

    label.classList.add('loading');
    label.querySelector('.loading-overlay__spinner').classList.remove('hidden');

    try {
      if (!nextStepElement) throw new Error(`No step with id quiz_step_${nextStep} found`);
      const jsonFilters = await this.fetchFilters();

      const filters = jsonFilters.filters;
      if (!filters) throw new Error('No filters found');

      const nextFilters = filters[filterKey];
      if (!nextFilters) throw new Error(`No filter with ${filterKey} key found`);

      if (nextFilters.should_be_hidden) {
        const nextStepContinueButtons = nextStepElement.querySelectorAll('[data-next-step]');
        const visibleNextStepContinueButton = Array.from(nextStepContinueButtons).find(button => window.getComputedStyle(button, null).display !== 'none');
        if (visibleNextStepContinueButton) {
          await this.nextStep(visibleNextStepContinueButton);
          shouldStopLoading = false;
          return;
        }

        const submitButton = nextStepElement.querySelector('button[type="submit"]');
        if (submitButton) submitButton.click();
        shouldStopLoading = false;
        return;
      }

      let liThatShouldBeVisible = [];

      nextStepElement.querySelectorAll(`input[name="${filterKey}"]`).forEach(input => {
        const li = input.closest('li');

        // Grouped values (Search & Discovery) are exposed as gids, so match on the label too
        const normalizedInput = normalize(input.value);
        const filterValue = nextFilters.values.find(value => value.value == input.value || normalize(value.label) === normalizedInput);
        if (filterValue) input.value = filterValue.value;
        if (!filterValue || filterValue.count === 0) {
          if (input.type === 'hidden') input.dataset.shouldBeDisabled = true;
          if (!liThatShouldBeVisible.includes(li)) li.style.display = 'none';
        } else {
          liThatShouldBeVisible.push(li);
          li.style.display = 'list-item';
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      label.classList.remove('loading');
      label.querySelector('.loading-overlay__spinner').classList.add('hidden');
      if (shouldStopLoading) {
        const input = this.querySelector(`#step_radio_${nextStep}`);
        if (input) {
          input.checked = true;
          input.dispatchEvent(new Event('change'));
        }
      }
    }
  }

  previousStep(event) {
    const label = event.currentTarget;
    const { previousStep } = label.dataset;
    const previousStepElement = this.querySelector(`#quiz_step_${previousStep}`);
    // Get all the next steps (.shirt-quiz__step) placed after the previousStepElement
    const nextSteps = Array.from(this.querySelectorAll('.shirt-quiz__step')).slice(Array.from(this.querySelectorAll('.shirt-quiz__step')).indexOf(previousStepElement) + 1);
    // Get all the inputs in each of the next steps (.shirt-quiz__step-label input) and uncheck them
    nextSteps.forEach(step => Array.from(step.querySelectorAll('.shirt-quiz__step-answer input')).forEach(input => {
      if (input.type === 'radio' || input.type === 'checkbox') input.checked = false;
      if (input.type === 'hidden') {
        input.disabled = true;
        delete input.dataset.shouldBeDisabled;
      }
      input.closest('li').style.display = 'list-item';
    }));
  }

  async update(event) {
    const input = event.currentTarget;
    if (input.name == 'occasion') {
      this.steps.dataset.occasion = input.value;
      switch (input.value) {
        case 'week_end':
          this.collectionUrl = window.shirtQuiz.casualCollectionUrl;
          break;
        case 'event':
          this.collectionUrl = window.shirtQuiz.formalCollectionUrl;
          break;
        default:
          this.collectionUrl = window.shirtQuiz.allCollectionUrl;
          break;
      }
      this.form.action = this.collectionUrl;
    }
    const currentStep = input.closest('[data-step]');
    const isStepChecked = currentStep.querySelectorAll(':checked').length > 0;
    if (isStepChecked) {
      currentStep.classList.add('--checked');
    } else {
      currentStep.classList.remove('--checked');
    }

    // Multiple values for one input
    if (input.dataset.hasMultipleValues) {
      const li = input.closest('li');
      const otherInputs = li.querySelectorAll('input[type="hidden"]');
      otherInputs.forEach(otherInput => {
        if (input.checked && otherInput.dataset.shouldBeDisabled !== 'true') {
          otherInput.disabled = false;
        } else if (!input.checked) {
          otherInput.disabled = true;
        }
      });
    }

    // Automatically go to next step if input is a radio button
    if (input.type == 'radio' && isStepChecked) {
      const nextStepButtons = currentStep.querySelectorAll('label[for*="step_radio"]');

      const button = Array.from(nextStepButtons).find(button => window.getComputedStyle(button, null).display !== 'none');
      if (button) button.click();
    }

    // if last step
    if (currentStep.querySelector('button[type="submit"]')) {
      const button = currentStep.querySelector('button[type="submit"]');
      button.classList.add('loading');
      button.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      // Abort the previous request and create a new AbortController for the new request
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      try {
        const jsonFilters = await this.fetchFilters({ signal: this.controller.signal });
        const productsCount = jsonFilters.productsCount;
        if (productsCount) this.totalCount.textContent = ` (${productsCount})`;

        button.classList.remove('loading');
        button.querySelector('.loading-overlay__spinner').classList.add('hidden');
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted');
        } else {
          console.error(error);
          button.classList.remove('loading');
          button.querySelector('.loading-overlay__spinner').classList.add('hidden');
        }
      }
    }
  }

  cancelRequest() {
    // 3. Call the abort method on the controller when you want to cancel the request
    this.controller.abort();
  }
}
customElements.define('shirt-quiz', ShirtQuiz);